import React from 'react';
import {
  Box,
  Collapse,
  InputAdornment,
  ListItemButton,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore, Search } from '@mui/icons-material';
import { FleetEntry, filterFleet, fleetLabel } from '../controllers/fleet';
import { ControllerSnapshot, SnapshotConnection, SnapshotDevice, SnapshotPoint } from './peripherySnapshot';
import { CatalogBlock } from './CatalogBlock';
import { PalettePayload, palettePreviewNode } from './catalogPreview';
import { useI18n } from '../i18n';

type LayoutTarget = { layoutId: string; title: string; widgetId: string };

function matches(query: string, ...parts: string[]): boolean {
  if (!query) {
    return true;
  }
  const hay = parts.join(' ').toLowerCase();
  return hay.includes(query);
}

function DragRow({ payload }: { payload: PalettePayload }) {
  const { t } = useI18n();
  return (
    <Box
      draggable
      onDragStart={(event) => {
        const packed = JSON.stringify(payload);
        event.dataTransfer.setData('application/vcs-catalog', packed);
        event.dataTransfer.setData('text/plain', packed);
        event.dataTransfer.effectAllowed = 'copy';
      }}
      sx={{
        px: 0.5,
        py: 0.6,
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
        '&:hover .catalog-block': {
          boxShadow: 4,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CatalogBlock node={palettePreviewNode(payload, t)} palette />
    </Box>
  );
}

const FlowPalette: React.FC<{
  query: string;
  onQuery: (value: string) => void;
  fleet: FleetEntry[];
  canvasControllers: { id: string; hostname: string }[];
  snapshots: Record<string, ControllerSnapshot>;
  layoutTargets: LayoutTarget[];
}> = ({ query, onQuery, fleet, canvasControllers, snapshots, layoutTargets }) => {
  const { t } = useI18n();
  const q = query.trim().toLowerCase();
  const [open, setOpen] = React.useState<Record<string, boolean>>({ fleet: true, bricks: true });
  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: prev[key] === false }));

  const canvasIds = new Set(canvasControllers.flatMap((item) => [item.id, item.hostname].filter(Boolean)));
  const onCanvasFleet = fleet.filter((entry) => canvasIds.has(entry.id) || canvasIds.has(entry.hostname));
  const extras = canvasControllers
    .filter((item) => !onCanvasFleet.some((entry) => entry.id === item.id || entry.hostname === item.hostname))
    .map((item) => ({ id: item.id, hostname: item.hostname, online: false } as FleetEntry));
  const seenHosts = new Set<string>();
  const onCanvas = [...onCanvasFleet, ...extras].filter((entry) => {
    if (seenHosts.has(entry.id)) {
      return false;
    }
    seenHosts.add(entry.id);
    return true;
  });
  const fleetHits = filterFleet(fleet, query);

  const layouts = layoutTargets.reduce<Record<string, LayoutTarget[]>>((acc, item) => {
    if (!matches(q, item.layoutId, item.title, item.widgetId)) {
      return acc;
    }
    (acc[item.layoutId] ||= []).push(item);
    return acc;
  }, {});

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t('flow.palette')}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('flow.paletteDragHint')}
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        placeholder={t('flow.paletteSearch')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />
      <SectionHeader
        open={open.fleet !== false}
        onClick={() => toggle('fleet')}
        label={t('flow.paletteFleet')}
      />
      <Collapse in={open.fleet !== false}>
        {fleetHits.map((entry) => (
          <DragRow
            key={entry.id}
            payload={{ kind: 'controller', fleetId: entry.id, hostname: entry.hostname, label: fleetLabel(entry) }}
          />
        ))}
        {!q && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block' }}>
            {t('flow.paletteControllerHint')}
          </Typography>
        )}
        {q && fleetHits.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>{t('flow.paletteControllerEmpty')}</Typography>
        )}
      </Collapse>

      {onCanvas.map((entry) => {
        const snap = snapshots[entry.id] || snapshots[entry.hostname];
        const key = `c:${entry.id}`;
        const show = matches(q, fleetLabel(entry), entry.hostname) || connectionVisible(snap, q);
        if (!show && q) {
          return null;
        }
        return (
          <Box key={entry.id} sx={{ mt: 1 }}>
            <SectionHeader
              open={open[key] !== false}
              onClick={() => toggle(key)}
              label={fleetLabel(entry)}
            />
            <Collapse in={open[key] !== false}>
              {!snap && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, display: 'block' }}>
                  {t('flow.paletteNoSettings')}
                </Typography>
              )}
              {snap && snap.connections.length === 0 && snap.devices.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, display: 'block' }}>
                  {t('flow.paletteNoConnections')}
                </Typography>
              )}
              {snap?.connections.map((conn) => (
                conn.type === 'mqtt' ? null : (
                <ConnectionBranch
                  key={conn.id}
                  ownerId={`g:${entry.id}`}
                  conn={conn}
                  devices={snap.devices.filter((device) => device.connection === conn.id)}
                  query={q}
                />
                )
              ))}
              {snap?.devices.filter((device) => !device.connection).map((device) => (
                <DeviceBranch key={device.id} ownerId={`g:${entry.id}`} device={device} query={q} />
              ))}
            </Collapse>
          </Box>
        );
      })}

      <SectionHeader
        open={open.bricks !== false}
        onClick={() => toggle('bricks')}
        label={t('flow.paletteBricks')}
        sx={{ mt: 1.5 }}
      />
      <Collapse in={open.bricks !== false}>
        {matches(q, 'timer', t('flow.brick.timer')) && (
          <DragRow payload={{ kind: 'timer' }} />
        )}
        {matches(q, 'calc', t('flow.brick.calc'), t('flow.calc')) && (
          <DragRow payload={{ kind: 'calc' }} />
        )}
        {matches(q, 'toggle', t('flow.brick.toggle'), t('flow.toggleHint')) && (
          <DragRow payload={{ kind: 'toggle' }} />
        )}
        {matches(q, 'mqtt', t('flow.brick.mqtt_topic'), t('flow.mqttTopic')) && (
          <DragRow payload={{ kind: 'mqtt_topic' }} />
        )}
        {matches(q, 'zigbee', t('flow.brick.zigbee_point'), t('flow.zigbeePointHint')) && (
          <DragRow payload={{ kind: 'zigbee_point' }} />
        )}
        {matches(q, 'threshold', t('flow.brick.threshold'), t('flow.chainDestAlert')) && (
          <DragRow payload={{ kind: 'threshold' }} />
        )}
        {matches(q, 'notify', t('flow.brick.notify'), t('flow.chainDestAlert')) && (
          <DragRow payload={{ kind: 'notify' }} />
        )}
        {matches(q, 'dashboard', t('flow.climateWidget')) && (
          <DragRow payload={{ kind: 'dashboard', widgetType: 'temperature' }} />
        )}
        {matches(q, 'relay', t('flow.relayWidget')) && (
          <DragRow payload={{ kind: 'dashboard', widgetType: 'relay' }} />
        )}
        {Object.entries(layouts).map(([layoutId, items]) => (
          <Box key={layoutId} sx={{ pl: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>{items[0]?.title || layoutId}</Typography>
            {items.map((item) => (
              <DragRow
                key={`${item.layoutId}:${item.widgetId}`}
                payload={{
                  kind: 'label',
                  layoutId: item.layoutId,
                  widgetId: item.widgetId,
                  title: item.title,
                }}
              />
            ))}
          </Box>
        ))}
      </Collapse>
    </Box>
  );
};

function connectionVisible(snap: ControllerSnapshot | undefined, q: string): boolean {
  if (!q || !snap) {
    return true;
  }
  return snap.connections.some((conn) => matches(q, conn.id, conn.type, ...conn.points.map((p) => p.key)))
    || snap.devices.some((device) => matches(q, device.id, device.type, ...device.points.map((p) => p.key)));
}

function ConnectionBranch({
  ownerId,
  conn,
  devices,
  query,
}: {
  ownerId: string;
  conn: SnapshotConnection;
  devices: SnapshotDevice[];
  query: string;
}) {
  const show = matches(query, conn.id, conn.type)
    || conn.points.some((point) => matches(query, point.key))
    || devices.some((device) => matches(query, device.id, ...device.points.map((p) => p.key)));
  if (query && !show) {
    return null;
  }
  return (
    <Box sx={{ pl: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>{conn.type} · {conn.label}</Typography>
      <DragRow
        payload={{
          kind: 'bus',
          ownerId,
          of: conn.id,
          busType: conn.type,
        }}
      />
      {conn.points.map((point) => (
        <PointRow key={point.key} ownerId={ownerId} of={conn.id} point={point} />
      ))}
      {devices.map((device) => (
        <DeviceBranch key={device.id} ownerId={ownerId} device={device} query={query} />
      ))}
    </Box>
  );
}

function DeviceBranch({
  ownerId,
  device,
  query,
}: {
  ownerId: string;
  device: SnapshotDevice;
  query: string;
}) {
  if (query && !matches(query, device.id, device.type, ...device.points.map((p) => p.key))) {
    return null;
  }
  return (
    <Box sx={{ pl: 0.5 }}>
      <Typography variant="caption" sx={{ px: 0.5 }}>{device.id}</Typography>
      <DragRow
        payload={{
          kind: 'device',
          ownerId,
          of: device.id,
          deviceType: device.type,
          connection: device.connection || '',
        }}
      />
      {device.points.map((point) => (
        <PointRow key={point.key} ownerId={ownerId} of={device.id} point={point} />
      ))}
    </Box>
  );
}

function PointRow({ ownerId, of, point }: { ownerId: string; of: string; point: SnapshotPoint }) {
  return (
    <DragRow
      payload={{
        kind: 'point',
        ownerId,
        of,
        key: point.key,
        poll: point.kind === 'poll' ? true : point.kind === 'event' ? false : undefined,
      }}
    />
  );
}

function SectionHeader({
  open,
  onClick,
  label,
  sx,
}: {
  open: boolean;
  onClick: () => void;
  label: string;
  sx?: object;
}) {
  return (
    <ListItemButton onClick={onClick} dense sx={{ px: 0.5, ...sx }}>
      {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>{label}</Typography>
    </ListItemButton>
  );
}

export default FlowPalette;
